import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootStateOrAny } from 'react-redux';

import type { ImageAnalyses } from '../../../../@types/latestImagesV2';
import type { PreferencesResponse } from '../../../../@types/preferences';
import type { TClaimTooth } from '../../../../@types/rcm';
import { makeAPIRequestReactQuery } from '../../../../react-query/api';
import { endpoints } from '../../../../react-query/endpoints';
import { useClaimVisit } from '../insuranceExport/hooks/useClaimVisit';
import type {
  GetClaimSummaryResponse,
  TImageDisplay,
  TTreatmentResponse,
} from '../insuranceExport/types';
import type { TView } from '../state';

export type TSupportedTreatmentType = 'SRP' | null;

/** Treatment with txGroupShortName and teeth derived from v8 for display/filter. */
export type TSummaryTreatment = TTreatmentResponse & {
  txGroupShortName: string;
  /** Tooth IDs for this treatment, derived from imagesDisplay. */
  teeth?: string[];
};

function buildDefaultTeethFromV8(
  imagesDisplay: TImageDisplay[] | null | undefined,
  treatments: TTreatmentResponse[],
): TClaimTooth[] {
  if (!imagesDisplay?.length) return [];

  const toothTxMap = new Map<string, Map<number, number[]>>();
  for (const img of imagesDisplay) {
    for (const tx of img.txs) {
      for (const tooth of tx.teeth) {
        if (!toothTxMap.has(tooth.toothId)) {
          toothTxMap.set(tooth.toothId, new Map());
        }
        const txMap = toothTxMap.get(tooth.toothId)!;
        if (!txMap.has(tx.txId)) txMap.set(tx.txId, []);
        txMap.get(tx.txId)!.push(img.id);
      }
    }
  }
  const treatmentByTxId = new Map(treatments.map((t) => [t.txId, t]));

  return Array.from(toothTxMap.entries()).map(([toothId, txMap]) => ({
    toothId,
    treatments: Array.from(txMap.entries()).map(([txId, imageIds]) => {
      const t = treatmentByTxId.get(txId);
      return {
        txId,
        txCode: t?.txCode ?? '',
        txGroupShortName: (t?.txGroupName ?? '').toLowerCase(),
        imageIds,
      };
    }),
    defaultSelected: true,
  }));
}

function formatDmfDisplay(dmf: number): string {
  return dmf < 0.05 ? '<5%' : `${Math.round(dmf * 100)}%`;
}

function buildDmfValuesFromV8(
  imagesDisplay: TImageDisplay[] | null | undefined,
): { toothNumber: string; dmf: string }[] {
  if (!imagesDisplay?.length) return [];

  const byTooth = new Map<string, number | null>();
  for (const img of imagesDisplay) {
    for (const tx of img.txs) {
      for (const tooth of tx.teeth) {
        if (tooth.dmf != null && !byTooth.has(tooth.toothId)) {
          byTooth.set(tooth.toothId, tooth.dmf);
        }
      }
    }
  }
  return Array.from(byTooth.entries())
    .filter(([, dmf]) => dmf != null)
    .map(([toothNumber, dmf]) => ({
      toothNumber,
      dmf: formatDmfDisplay(dmf as number),
    }));
}

function buildDmfValuesFromImages(
  images: ImageAnalyses[] | null | undefined,
): { toothNumber: string; dmf: string }[] {
  if (!images?.length) return [];

  const byTooth = new Map<string, number | null>();
  for (const img of images) {
    for (const tooth of img.teeth ?? []) {
      if (tooth.dmf != null && !byTooth.has(tooth.toothId)) {
        byTooth.set(tooth.toothId, tooth.dmf);
      }
    }
  }
  return Array.from(byTooth.entries())
    .filter(([, dmf]) => dmf != null)
    .map(([toothNumber, dmf]) => ({
      toothNumber,
      dmf: formatDmfDisplay(dmf as number),
    }));
}

const useImagesV8 = ({
  estimatedVisitId,
  unifiedAppointmentId,
  organizationId,
  practiceId,
}: {
  estimatedVisitId: number;
  unifiedAppointmentId?: number;
  organizationId: string;
  practiceId: string;
}) => {
  const path = `/v8/${endpoints.claimFindings}`;

  return useQuery(
    [path, estimatedVisitId, unifiedAppointmentId],
    async () => {
      const resp = await makeAPIRequestReactQuery<GetClaimSummaryResponse>(
        path,
        {},
        'POST',
        {
          data: {
            ...(Number.isFinite(estimatedVisitId) ? { estimatedVisitId } : {}),
            unifiedAppointmentId,
          },
          headers: {
            Org: organizationId,
            Practice: practiceId,
          },
        },
      );
      return resp;
    },
    {
      enabled: !!(
        unifiedAppointmentId && Number.isFinite(unifiedAppointmentId)
      ),
    },
  );
};

export const useSummaryImages = ({
  unifiedAppointmentId,
  defaultEstimatedVisitId,
  defaultImagingPatientId,
  appointmentImagingPatientId,
  appointmentStart,
}: {
  unifiedAppointmentId?: number;
  defaultEstimatedVisitId?: string;
  defaultImagingPatientId?: string;
  appointmentImagingPatientId?: string;
  appointmentStart?: string;
  preferences: PreferencesResponse;
}) => {
  const {
    estimatedVisitId: customizeSummaryVisitId,
    isLoading: isLoadingCustomizeSummary,
    visits,
  } = useClaimVisit({
    imagingPatientId: appointmentImagingPatientId || defaultImagingPatientId,
    appointmentOn: appointmentStart,
  });

  const visitData = visits.find((v) => v.id === customizeSummaryVisitId);
  const estimatedVisitId = defaultEstimatedVisitId
    ? Number(defaultEstimatedVisitId)
    : Number(visitData?.id);

  const { primaryPracticeId: practiceId, primaryOrganizationId: orgId } =
    useSelector((state: RootStateOrAny) => state.login.user);

  const { data, isLoading } = useImagesV8({
    estimatedVisitId,
    unifiedAppointmentId,
    organizationId: orgId,
    practiceId,
  });

  const sortedTreatments = useMemo((): TSummaryTreatment[] => {
    const list = data?.treatments ?? [];
    const txToTeeth = new Map<number, string[]>();
    for (const img of data?.imagesDisplay ?? []) {
      for (const tx of img.txs) {
        const ids = tx.teeth.map((tooth) => tooth.toothId);
        if (!txToTeeth.has(tx.txId)) txToTeeth.set(tx.txId, []);
        const set = new Set(txToTeeth.get(tx.txId)!);
        ids.forEach((id) => set.add(id));
        txToTeeth.set(tx.txId, Array.from(set));
      }
    }
    const withShortAndTeeth = list.map((t) => ({
      ...t,
      txGroupShortName: (t.txGroupName ?? '').toLowerCase(),
      teeth: txToTeeth.get(t.txId) ?? (t.toothId ? [t.toothId] : []),
    }));
    return withShortAndTeeth.sort((a, b) => {
      if (a.txGroupShortName !== b.txGroupShortName) {
        return (
          (b.txGroupShortName === 'srp' ? 1 : 0) -
          (a.txGroupShortName === 'srp' ? 1 : 0)
        );
      }
      if (a.txGroupShortName && b.txGroupShortName) {
        return a.txGroupShortName.localeCompare(b.txGroupShortName);
      }
      return 0;
    });
  }, [data?.treatments, data?.imagesDisplay]);

  const defaultTeeth = useMemo(
    () => buildDefaultTeethFromV8(data?.imagesDisplay, data?.treatments ?? []),
    [data?.imagesDisplay, data?.treatments],
  );

  const dmfValues = useMemo(() => {
    const fromDisplay = buildDmfValuesFromV8(data?.imagesDisplay);
    if (fromDisplay.length > 0) return fromDisplay;
    return buildDmfValuesFromImages(data?.images ?? []);
  }, [data?.imagesDisplay, data?.images]);

  const supportedTreatmentType: TSupportedTreatmentType = useMemo(() => {
    const groups = new Set(
      (data?.treatments ?? []).map((t) => (t.txGroupName ?? '').toLowerCase()),
    );
    return groups.size === 1 && groups.has('srp') ? 'SRP' : null;
  }, [data?.treatments]);

  const supportedTreatmentGroups = useMemo(
    () =>
      Array.from(
        new Set(
          (data?.treatments ?? []).map((t) =>
            (t.txGroupName ?? '').toLowerCase(),
          ),
        ),
      ) || null,
    [data?.treatments],
  );

  return {
    treatments: sortedTreatments,
    images: data?.images ?? [],
    messages: data?.messages ?? [],
    supportedTreatmentType,
    isLoading: isLoadingCustomizeSummary || isLoading,
    dmfValues,
    defaultTeeth,
    supportedTreatmentGroups,
    findingsPatientName: data?.patientName,
    findingsPatientDateOfBirth: data?.patientDateOfBirth,
    findingsDateOfService: data?.dateOfService,
    findingsProviderName: data?.providerName,
  };
};

export const useSummaryVisit = ({
  appointmentStart,
  appointmentImagingPatientId,
  selectedVisitId,
  selectedPatientId,
  insView,
}: {
  appointmentStart?: string;
  imagingPatientId?: string;
  estimatedVisitId?: number;
  selectedVisitId: string | null;
  selectedPatientId: string | null;
  appointmentImagingPatientId: string | null;
  insView: TView;
}) => {
  const {
    estimatedVisitId: customizeSummaryVisitId,
    imagingPatientId: customizeSummaryImagingPatientId,
    isLoading: isLoadingCustomizeSummary,
    visits,
  } = useClaimVisit({
    imagingPatientId: appointmentImagingPatientId || selectedPatientId || '',
    appointmentOn: appointmentStart,
  });

  const {
    estimatedVisitId,
    imagingPatientId,
    visitDate,
    radiographsTakenDate,
  } = useMemo(() => {
    // opening from customize button
    if (insView === 'appointment') {
      const selectedVisit = visits.find(
        (v) => v.id === customizeSummaryVisitId,
      );

      return {
        estimatedVisitId: customizeSummaryVisitId
          ? String(customizeSummaryVisitId)
          : null,
        imagingPatientId: customizeSummaryImagingPatientId || '',
        visitDate: appointmentStart || '',
        radiographsTakenDate: selectedVisit?.visitDate || null,
      };
    }

    // opening from image viewer sidebar
    return {
      estimatedVisitId: selectedVisitId,
      imagingPatientId: selectedPatientId || '',
      visitDate: visits.find((v) => v.id === Number(selectedVisitId))
        ?.visitDate,
      radiographsTakenDate: null,
    };
  }, [
    selectedVisitId,
    selectedPatientId,
    customizeSummaryVisitId,
    customizeSummaryImagingPatientId,
    appointmentStart,
    visits,
    insView,
  ]);

  return {
    estimatedVisitId,
    imagingPatientId,
    visitDate,
    radiographsTakenDate: radiographsTakenDate?.toString() ?? '',
    isLoading: isLoadingCustomizeSummary,
  };
};
